// L11 (boot-health #3986): bound CsSQLite.open so a hung native SQLite open can't leave every DB
// operation pending forever. The IndexedDB path has recovery (interceptDatabaseError →
// indexed_db_went_bad); the SQLite adapter did not, so a stuck open was a silent iOS-native boot
// wedge. On timeout we reject with a DISTINGUISHABLE error (name = SqliteOpenTimeout) so the
// adapter's callback surface propagates it and the wrapper/fatal-error layer above
// (DatabaseRecoveryService.interceptDatabaseError) can report SqliteOpenTimeout and route to the reload
// path. This adapter is a low-level PouchDB module with no LoggingService — it only signals via
// the error; it never logs.
//
// Wall time, checked in #6096 (2026-10-01): its one Sentry report did not follow a freeze, and an
// open takes milliseconds, so a phone freezing the app inside one is rare. Firing reloads the page,
// so if a report ever shows a freeze before it, move it onto running time (running-clock.ts).

export const SQLITE_OPEN_TIMEOUT_MS = 15_000;

// The distinguishing error name. interceptDatabaseError matches on it; keep the two in sync.
export const SQLITE_OPEN_TIMEOUT_NAME = 'SqliteOpenTimeout';

export function isSqliteOpenTimeoutError(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: string }).name === SQLITE_OPEN_TIMEOUT_NAME;
}

export type ScheduleTimeout = (cb: () => void, ms: number) => unknown;
export type ClearTimeoutFn = (handle: unknown) => void;

/**
 * Race an open() promise against a timeout. Resolves/rejects with the open's outcome if it
 * settles first; on timeout, rejects with a distinguishable SqliteOpenTimeout error. The timer is
 * always cleared so it can't leak, and the result is latched so a late open result after a
 * timeout is ignored.
 */
export function withSqliteOpenTimeout<T>(
  open: Promise<T>,
  dbName: string,
  timeoutMs: number,
  scheduleTimeout: ScheduleTimeout,
  clearTimeoutFn: ClearTimeoutFn,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const handle = scheduleTimeout(() => {
      if (settled) return;
      settled = true;
      const err = new Error(`CsSQLite.open timed out after ${timeoutMs}ms for '${dbName}'`);
      err.name = SQLITE_OPEN_TIMEOUT_NAME;
      reject(err);
    }, timeoutMs);
    open.then(
      value => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        resolve(value);
      },
      err => {
        if (settled) return;
        settled = true;
        clearTimeoutFn(handle);
        reject(err);
      },
    );
  });
}
